import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { base44, setActiveOrgId, clearActiveOrgId } from '@/api/base44Client';
import { useAuth } from '@/lib/AuthContext';
import { queryClientInstance } from '@/lib/query-client';

const OrganisationContext = createContext(null);

export function OrganisationProvider({ children }) {
  const [organisations, setOrganisations] = useState([]);
  const [actAsTargets, setActAsTargets] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [actingAs, setActingAs] = useState(null);
  const [organisationError, setOrganisationError] = useState('');
  const [loading, setLoading] = useState(true);
  const [tenantTransitioning, setTenantTransitioning] = useState(false);
  const [tenantRevision, setTenantRevision] = useState(0);
  const selectedOrgRef = useRef(null);
  const organisationChangeGuard = useRef(null);
  const registerOrganisationChangeGuard = useCallback(guard => {
    organisationChangeGuard.current = guard;
    return () => { if (organisationChangeGuard.current === guard) organisationChangeGuard.current = null; };
  }, []);
  // Every bootstrap/poll gets a generation. Explicit tenant transitions bump it
  // before their request starts, so an older poll cannot restore stale state.
  const tenantRequestGeneration = useRef(0);
  const tenantTransitioningRef = useRef(false);
  const { isAuthenticated, authChecked, isLoadingAuth } = useAuth();

  const clearTenantCaches = useCallback(() => { queryClientInstance.clear(); }, []);

  const clearLocalTenant = useCallback((invalidate = true) => {
    if (invalidate) tenantRequestGeneration.current += 1;
    tenantTransitioningRef.current = false;
    clearActiveOrgId();
    setTenantRevision(v => v + 1);
    selectedOrgRef.current = null;
    setSelectedOrg(null);
    setActingAs(null);
    setOrganisations([]);
    setActAsTargets([]);
    clearTenantCaches();
  }, [clearTenantCaches]);

  const applySelectedOrg = useCallback((next) => {
    tenantRequestGeneration.current += 1;
    setSelectedOrg(prev => {
      selectedOrgRef.current = next;
      if (prev?.id !== next?.id || prev?.status !== next?.status) {
        setTenantRevision(v => v + 1);
        clearTenantCaches();
        return next;
      }
      return prev;
    });
    setActiveOrgId(next?.id);
  }, [clearTenantCaches]);

  const loadOrganisations = useCallback(async ({ preserveTenant = false } = {}) => {
    const generation = ++tenantRequestGeneration.current;
    if (!preserveTenant) {
      tenantTransitioningRef.current = true;
      setTenantTransitioning(true);
      setLoading(true);
    }
    try {
      // Bootstrap is tenant-free. Only this response can restore an act-as target;
      // browser storage is a display hint and never an authorisation grant.
      const confirmed = await base44.auth.me();
      if (generation !== tenantRequestGeneration.current) return null;
      const confirmedActingAs = confirmed?.acting_as ?? null;
      const all = await base44.entities.Organisation.list('-created_date', 50, { skipTenant: true });
      if (generation !== tenantRequestGeneration.current) return null;
      const providerMembershipIds = new Set((confirmed?.organisations ?? []).filter(o => o.is_provider).map(o => o.id));
      const provider = !!confirmed?.is_provider;
      const list = provider
        ? all.filter(o => providerMembershipIds.has(o.id) || (confirmedActingAs && o.id === confirmedActingAs.organisation_id))
        : all;
      setOrganisationError('');
      if (provider) {
        setActAsTargets(all.filter(o => o.is_provider === false && (o.status || 'active') === 'active'));
        if (!confirmedActingAs) clearActiveOrgId();
      } else {
        setActAsTargets([]);
      }
      setActingAs(confirmedActingAs);
      setOrganisations(list);
      const ownProviderId = (confirmed?.organisations ?? []).find(o => o.is_provider)?.id;
      let nextOrg = confirmedActingAs ? list.find(o => o.id === confirmedActingAs.organisation_id) : null;
      if (!nextOrg && provider && ownProviderId) nextOrg = list.find(o => o.id === ownProviderId) || null;
      if (!nextOrg && !provider) {
        let saved = null; try { saved = localStorage.getItem('ic_active_org'); } catch {}
        const confirmedOrg = list.find(o => o.id === confirmed?.organisation_id);
        const savedOrg = list.find(o => o.id === saved && o.status !== 'archived');
        // Restore only a saved membership returned by the server, including a
        // suspended selection that needs the recovery screen. Provider act-as
        // remains driven by the server grant above.
        nextOrg = savedOrg || confirmedOrg || list.find(o => (o.status || 'active') === 'active') || list[0] || null;
      }
      if (generation !== tenantRequestGeneration.current) return null;
      const previousSelected = selectedOrgRef.current;
      const refreshedPrevious = previousSelected && list.find(o => o.id === previousSelected.id && (o.status || 'active') !== 'archived');
      const selectedForStorage = confirmedActingAs ? nextOrg : (refreshedPrevious || nextOrg);
      setSelectedOrg(prev => {
        // Keep an explicitly selected active membership through background polls;
        // the server's /me membership list is authoritative for status changes.
        selectedOrgRef.current = selectedForStorage;
        if (prev?.id !== selectedForStorage?.id || prev?.status !== selectedForStorage?.status) {
          setTenantRevision(v => v + 1);
          clearTenantCaches();
          return selectedForStorage;
        }
        return prev;
      });
      setActiveOrgId(selectedForStorage?.id);
      return confirmed;
    } catch (e) {
      if (generation !== tenantRequestGeneration.current) return null;
      console.error('Failed to load organisations', e);
      clearLocalTenant(false);
      setOrganisationError(e?.message || 'Organisation access needs recovery. Reload to continue.');
      throw e;
    } finally {
      if (!preserveTenant && generation === tenantRequestGeneration.current) {
        tenantTransitioningRef.current = false;
        setLoading(false);
        setTenantTransitioning(false);
      }
    }
  }, [clearLocalTenant, clearTenantCaches]);

  useEffect(() => {
    // Initial authentication is unresolved, not a sign-out. Preserve the saved
    // selection until /me confirms the session and its actual memberships.
    if (!authChecked || isLoadingAuth) return;
    if (isAuthenticated) loadOrganisations().catch(() => {});
    else { clearLocalTenant(); setOrganisationError(''); setLoading(false); }
  }, [isAuthenticated, authChecked, isLoadingAuth, loadOrganisations, clearLocalTenant]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const recoverInBackground = () => {
      if (tenantTransitioningRef.current) return;
      loadOrganisations({ preserveTenant: true }).catch(() => {});
    };
    const unsubscribe = base44.entities.Organisation.subscribe(recoverInBackground);
    const onStorage = (event) => { if (event.key === 'ic_active_org') recoverInBackground(); };
    // A denied request means the current page may already be stale. Gate the
    // outlet while the server confirms the replacement tenant.
    const onTenantDenied = () => { loadOrganisations().catch(() => {}); };
    window.addEventListener('storage', onStorage);
    window.addEventListener('ic:tenant-denied', onTenantDenied);
    return () => {
      unsubscribe();
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('ic:tenant-denied', onTenantDenied);
    };
  }, [isAuthenticated, loadOrganisations]);

  useEffect(() => {
    if (!actingAs) return undefined;
    const expiresAt = new Date(actingAs.expires_at).getTime();
    // An expired act-as grant must not schedule an immediate reload forever. The next
    // bootstrap will reconcile the server grant; until then, leave the current tenant stable.
    if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) return undefined;
    const delay = expiresAt - Date.now() + 100;
    const timer = window.setTimeout(() => { loadOrganisations().catch(() => {}); }, delay);
    return () => window.clearTimeout(timer);
  }, [actingAs, loadOrganisations]);

  const selectOrganisation = useCallback(async (orgId) => {
    const org = organisations.find(o => o.id === orgId);
    if (!org || orgId === selectedOrgRef.current?.id) return;
    const generation = tenantRequestGeneration.current;
    if (organisationChangeGuard.current && !(await organisationChangeGuard.current())) return;
    // A membership refresh or another transition may have superseded this choice.
    if (generation !== tenantRequestGeneration.current) return;
    applySelectedOrg(org);
  }, [organisations, applySelectedOrg]);

  const beginActAs = useCallback(async (organisationId, reason, { shouldApply = () => true } = {}) => {
    if (organisationChangeGuard.current && !(await organisationChangeGuard.current())) throw new Error('Save your assessment progress before changing organisation.');
    tenantRequestGeneration.current += 1;
    tenantTransitioningRef.current = true;
    setTenantTransitioning(true);
    try {
      const result = await base44.actAs.begin(organisationId, reason);
      // A caller can become stale while the server creates the grant (for example,
      // a routed customer detail being replaced). End that grant before any target
      // is applied to the global tenant context.
      if (!shouldApply()) {
        await base44.actAs.stop();
        clearActiveOrgId();
        clearTenantCaches();
        await loadOrganisations();
        return { ...result, cancelled: true };
      }
      clearActiveOrgId();
      clearTenantCaches();
      await loadOrganisations();
      return result;
    } catch (e) {
      tenantTransitioningRef.current = false;
      setTenantTransitioning(false);
      throw e;
    }
  }, [loadOrganisations, clearTenantCaches]);

  const stopActAs = useCallback(async () => {
    if (organisationChangeGuard.current && !(await organisationChangeGuard.current())) throw new Error('Save your assessment progress before changing organisation.');
    tenantRequestGeneration.current += 1;
    tenantTransitioningRef.current = true;
    setTenantTransitioning(true);
    try {
      await base44.actAs.stop();
      clearActiveOrgId();
      clearTenantCaches();
      await loadOrganisations();
    } catch (e) {
      tenantTransitioningRef.current = false;
      setTenantTransitioning(false);
      throw e;
    }
  }, [loadOrganisations, clearTenantCaches]);

  return (
    <OrganisationContext.Provider value={{
      organisations, actAsTargets, selectedOrg, selectOrganisation, registerOrganisationChangeGuard, loading,
      organisationError, reloadOrganisations: loadOrganisations, recoverActingAs: loadOrganisations,
      actingAs, beginActAs, stopActAs, tenantTransitioning, tenantRevision,
    }}>
      {children}
    </OrganisationContext.Provider>
  );
}

export function useOrganisation() {
  const context = useContext(OrganisationContext);
  if (!context) throw new Error('useOrganisation must be used within OrganisationProvider');
  return context;
}
